import { useState, useEffect, useCallback } from 'react';

/**
 * ActivityFeed — reverse-chronological activity feed for pod views.
 *
 * Groups activities by relative date (Today, Yesterday, This Week, Earlier).
 * Each entry shows emoji avatar(s), action text, item title, and relative date.
 *
 * @param {Object} props
 * @param {Object} props.activityService - ActivityService instance
 * @param {string} [props.podId] - Pod to filter activities for
 * @param {Array} props.users - Array of { id, name, displayName, emoji }
 * @param {Function} [props.onItemClick] - Called with activity when tapped
 * @param {Function} [props.getImageUrl] - (activity) => poster URL string
 */
export default function ActivityFeed({ activityService, podId, users = [], onItemClick, getImageUrl }) {
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchActivities = useCallback(async () => {
        if (!activityService) return;
        setLoading(true);
        setError(null);
        try {
            const result = await activityService.list({ podId, limit: 100 });
            setActivities(result.items || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [activityService, podId]);

    useEffect(() => {
        fetchActivities();
    }, [fetchActivities]);

    const userMap = {};
    users.forEach((u) => { userMap[u.id] = u; });

    const groupActivities = (items) => {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
        const weekAgo = new Date(today); weekAgo.setDate(weekAgo.getDate() - 7);

        const groups = { Today: [], Yesterday: [], 'This Week': [], Earlier: [] };

        items.forEach((item) => {
            const d = new Date(item.date || item.created);
            if (d >= today) groups.Today.push(item);
            else if (d >= yesterday) groups.Yesterday.push(item);
            else if (d >= weekAgo) groups['This Week'].push(item);
            else groups.Earlier.push(item);
        });

        return Object.entries(groups).filter(([, items]) => items.length > 0);
    };

    const actionVerb = (type) => {
        switch (type) {
            case 'watched': return 'watched';
            case 'played': return 'played';
            case 'read': return 'read';
            case 'finished': return 'finished';
            default: return 'added';
        }
    };

    const itemTypeIcon = (type) => {
        switch (type) {
            case 'movie': return '🎬';
            case 'season': return '📺';
            case 'game': return '🎮';
            case 'book': return '📚';
            case 'boardgame': return '🎲';
            default: return '📝';
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="w-5 h-5 border-2 border-text-tertiary border-t-accent-primary rounded-full animate-spin" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-center py-12">
                <p className="text-sm text-red-400">{error}</p>
                <button onClick={fetchActivities} className="mt-2 text-xs text-accent-primary hover:underline">
                    Try again
                </button>
            </div>
        );
    }

    if (activities.length === 0) {
        return (
            <div className="text-center py-12">
                <p className="text-lg mb-1">📋</p>
                <p className="text-sm text-text-tertiary font-body">No activity yet</p>
                <p className="text-xs text-text-tertiary font-body mt-1">
                    Activities will appear here when pod members track items together
                </p>
            </div>
        );
    }

    const grouped = groupActivities(activities);

    return (
        <div className="divide-y divide-border-subtle">
            {grouped.map(([label, items]) => (
                <div key={label} className="py-3">
                    <h3 className="px-4 text-[10px] font-medium uppercase tracking-wider text-text-tertiary mb-2">
                        {label}
                    </h3>
                    <ul className="space-y-1">
                        {items.map((activity) => {
                            const participants = (activity.participants || []).map((id) => userMap[id]).filter(Boolean);
                            const emojis = participants.map((u) => u.emoji).join('');
                            const verb = actionVerb(activity.type);
                            const icon = itemTypeIcon(activity.itemType);
                            const imageUrl = getImageUrl?.(activity);

                            return (
                                <li
                                    key={activity.id}
                                    onClick={() => onItemClick?.(activity)}
                                    className={`flex items-center gap-3 px-4 py-2.5 ${onItemClick ? 'cursor-pointer hover:bg-surface-hover' : ''} transition-colors`}
                                >
                                    {/* Participant emojis */}
                                    <span className="text-base flex-shrink-0 w-8 text-center" title={participants.map((u) => u.displayName || u.name).join(', ')}>
                                        {emojis || icon}
                                    </span>

                                    {/* Thumbnail */}
                                    {imageUrl && (
                                        <img
                                            src={imageUrl}
                                            alt=""
                                            className="w-8 h-11 rounded object-cover flex-shrink-0"
                                            loading="lazy"
                                        />
                                    )}

                                    {/* Text */}
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm text-text-primary font-body truncate">
                                            <span className="text-text-secondary">
                                                {participants.length > 0
                                                    ? participants.map((u) => u.displayName || u.name).join(' & ')
                                                    : 'Someone'}
                                            </span>
                                            {' '}{verb}{' '}
                                            <span className="font-medium">{activity.itemTitle || 'an item'}</span>
                                        </p>
                                        {activity.notes && (
                                            <p className="text-xs text-text-tertiary truncate mt-0.5">{activity.notes}</p>
                                        )}
                                    </div>

                                    {/* Type icon */}
                                    <span className="text-xs flex-shrink-0">{icon}</span>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            ))}
        </div>
    );
}
